import { DialogCloseEvent, DialogComponent } from "./dialog.js";
import { QuickViewDrawerOpenEvent, ThemeEvents } from "@theme/events";
import { BUNDLE_FLOW_UPDATE_EVENT, bundleState } from "@theme/nugget-bundle";
import { CartUpdateEvent } from "@theme/events";

/**
 * @typedef {{ didError?: boolean, source?: string, sellingPlanId?: string }} CartUpdateData
 * @typedef {{ data?: CartUpdateData, resource?: unknown, sourceId?: string }} CartUpdateDetail
 * @typedef {Event & { detail?: CartUpdateDetail }} CartUpdateLikeEvent
 *
 * @typedef {{ html?: Document, productId?: string, newProduct?: { id?: string, url?: string } }} VariantUpdateData
 * @typedef {{ featured_media?: { id?: string | number } }} VariantResource
 * @typedef {{ data?: VariantUpdateData, resource?: VariantResource }} VariantUpdateDetail
 * @typedef {Event & { detail?: VariantUpdateDetail }} VariantUpdateLikeEvent
 *
 * @typedef {HTMLElement & { getSelectedVariantId?: () => string | number | null }} ProductCardApi
 *
 * @typedef {HTMLElement & { selectSubscription?: (event?: Event | null, forceEmit?: boolean) => void, selectOneTime?: () => void }} GRProductOptionsElement
 *
 * @typedef {HTMLElement & { select?: (slide: { id: string }, next?: unknown, options?: { animate?: boolean }) => void }} SlideshowComponentElement
 */

class QuickViewDrawer extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Document>} */
  #cachedPages = new Map();

  /** @type {Map<string, Promise<Document | null>>} */
  #pendingFetches = new Map();

  /** @type {HTMLElement | null} */
  #optionsElement = null;

  /** @type {string | null} */
  #currentProductUrl = null;

  /** @type {string} */
  #namespaceToken = "qv";

  /** @type {number} */
  #openRequestToken = 0;

  /** @type {boolean} */
  #isNuggetQuickView = false;

  /** @type {boolean} */
  #isGiftBoxQuickView = false;

  /** @type {AbortController | null} */
  #accordionAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      QuickViewDrawerOpenEvent.eventName,
      this.#handleOpenEvent,
    );
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      BUNDLE_FLOW_UPDATE_EVENT,
      this.#handleBundleFlowUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      QuickViewDrawerOpenEvent.eventName,
      this.#handleOpenEvent,
    );
    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose,
    );
    this.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
    );
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      BUNDLE_FLOW_UPDATE_EVENT,
      this.#handleBundleFlowUpdate,
    );
    this.#abortController?.abort();
    this.#accordionAbortController?.abort();
  }

  /**
   * @param {Event} event
   */
  #handleOpenEvent = async (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;

    // For nugget-product-card, look for hidden product-card inside it
    const nuggetCard = target?.closest("nugget-product-card");
    const card = nuggetCard
      ? nuggetCard.querySelector("product-card")
      : target?.closest("product-card");
    if (!(card instanceof HTMLElement)) return;

    const token = ++this.#openRequestToken;

    // Track if this is a nugget quick view for styling
    this.#isNuggetQuickView = !!nuggetCard;

    // Track if this is a gift-box product
    this.#isGiftBoxQuickView = card.hasAttribute("data-is-gift-box");

    this.#render(card);
    this.#initNuggetAccordion();
    this.#applyGiftBoxStyles();

    await this.#mountProductControls(card);
    if (token !== this.#openRequestToken) return;

    this.showDialog();
    requestAnimationFrame(() => {
      if (token !== this.#openRequestToken) return;
      this.#trackQuickViewViewed(card);
    });
  };

  #handleDialogClose = () => {
    window.grAnalytics?.trackQuickviewClosed();
  };

  #getQuickViewListContext(card) {
    return (
      card.dataset.listContext ||
      card.dataset.collectionList ||
      card.dataset.collectionUrl ||
      card.dataset.sourceContext ||
      undefined
    );
  }

  #getQuickViewProductName(card) {
    const titleLink = card.querySelector('[ref="productTitleLink"]');
    const text =
      titleLink?.textContent ||
      card.querySelector(".product-card__link .visually-hidden")?.textContent ||
      "";

    const trimmed = text.replace(/\s+/g, " ").trim();
    return trimmed || undefined;
  }

  #trackQuickViewViewed(card) {
    const productId = card.dataset.productId || undefined;
    const productName = this.#getQuickViewProductName(card);
    const listContext = this.#getQuickViewListContext(card);

    const products = [];
    if (productId || productName) {
      products.push({
        id: productId,
        name: productName,
      });
    }

    const detailContext =
      listContext || products.length
        ? {
            detail: {
              actionField: listContext ? { list: listContext } : undefined,
              products: products,
            },
          }
        : undefined;

    window.grAnalytics?.trackQuickviewViewed({
      product_id: productId,
      product_name: productName,
      ...(detailContext ? { ecommerce: detailContext } : {}),
    });
  }

  /**
   * After a successful one-time add-to-cart from THIS drawer, open the GR cart drawer.
   * Subscription adds are handled by `gift-selection-drawer`.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const detail = /** @type {CartUpdateLikeEvent} */ (event).detail;

    if (detail?.data?.didError) return;
    if (detail?.data?.source !== "product-form-component") return;

    if (!(event.target instanceof HTMLElement)) return;
    if (!this.contains(event.target)) return;

    const sellingPlanId = detail?.data?.sellingPlanId;
    if (sellingPlanId) {
      this.closeDialog();
      return;
    }

    this.closeDialog();
    document.dispatchEvent(new Event("gr-cart:open"));
  };

  /**
   * Update Add to Cart button text when bundle flow state changes
   */
  #handleBundleFlowUpdate = () => {
    this.#updateAddToCartButtonText();
  };

  /**
   * Updates the "Add to Cart" button text based on bundle flow state
   */
  #updateAddToCartButtonText = () => {
    const formHost = this.refs.formHost;
    if (!(formHost instanceof HTMLElement)) return;

    const button = formHost.querySelector(
      'button[type="submit"][data-button-text-default]',
    );
    const label = button?.querySelector("[data-button-label]");

    if (!button || !label) return;

    const isBundleFlow = bundleState.bundleFlow;
    const defaultText = button.dataset.buttonTextDefault || "Add to Cart";
    const bundleText = button.dataset.buttonTextBundle || "Add to Bundle";

    // Only update if the button is not showing "Sold Out" or other non-standard text
    const currentText = label.textContent?.trim();
    if (
      currentText &&
      currentText !== "Sold Out" &&
      currentText !== "Unavailable"
    ) {
      label.textContent = isBundleFlow ? bundleText : defaultText;
    }
  };

  /**
   * Keep drawer UI in sync when the mounted `variant-picker` updates.
   * @param {Event} event
   */
  #handleVariantUpdate = async (event) => {
    const variantHost = this.refs.variantHost;
    if (!(variantHost instanceof HTMLElement)) return;
    if (!(event.target instanceof Node)) return;
    if (!variantHost.contains(event.target)) return;

    const detail = /** @type {VariantUpdateLikeEvent} */ (event).detail;
    const html = detail?.data?.html;
    const resource = detail?.resource;
    if (!(html instanceof Document)) return;

    this.#updateGalleryForVariant(resource);
    this.#updateContentsFromHtml(html);

    // Ensure the refreshed form is listening before options emits selling plan updates.
    await this.#refreshProductFormFromHtml(html);
    await this.#refreshProductOptionsFromHtml(html);

    // Variant-picker morphing can reintroduce conflicting ids/names; re-namespace.
    const picker = variantHost.querySelector("variant-picker");
    if (picker instanceof HTMLElement) this.#namespaceVariantPicker(picker);
    const optionsHost = this.refs.optionsHost;
    if (optionsHost instanceof HTMLElement) {
      const options = optionsHost.querySelector(
        "gr-product-options",
      );
      if (options instanceof HTMLElement) this.#namespaceIds(options);
    }

    const formHost = this.refs.formHost;
    if (formHost instanceof HTMLElement) {
      const form = formHost.querySelector("product-form-component");
      if (form instanceof HTMLElement) this.#namespaceIds(form);
    }
  };

  /**
   * Force `gr-product-options` to emit a SellingPlanUpdateEvent so the mounted
   * `product-form-component` receives the current selection.
   */
  #reEmitPurchaseSelectionForForm() {
    const optionsHost = this.refs.optionsHost;
    if (!(optionsHost instanceof HTMLElement)) return;

    const options = optionsHost.querySelector("gr-product-options");
    if (!(options instanceof HTMLElement)) return;

    const subscriptionSelected =
      options
        .querySelector('[ref="subscriptionOption"]')
        ?.getAttribute("data-selected") === "true";

    const optionsEl = /** @type {GRProductOptionsElement} */ (options);

    requestAnimationFrame(() => {
      if (subscriptionSelected) {
        optionsEl.selectSubscription?.(null, true);
      } else {
        optionsEl.selectOneTime?.();
      }
    });
  }

  /**
   * Fetches the product page content (with caching and deduplication)
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const cached = this.#cachedPages.get(productPageUrl);
    if (cached) return cached;

    const pending = this.#pendingFetches.get(productPageUrl);
    if (pending) return pending;

    const fetchPromise = this.#doFetch(productPageUrl);
    this.#pendingFetches.set(productPageUrl, fetchPromise);

    try {
      const result = await fetchPromise;
      if (result) {
        this.#cachedPages.set(productPageUrl, result);
      }
      return result;
    } finally {
      this.#pendingFetches.delete(productPageUrl);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #doFetch(productPageUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, "text/html");
    } catch (error) {
      const err = /** @type {unknown} */ (error);

      if (err instanceof DOMException && err.name === "AbortError") {
        return null;
      }

      if (typeof err === "object" && err !== null && "name" in err) {
        const name = /** @type {{ name?: unknown }} */ (err).name;
        if (name === "AbortError") return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLElement} card
   */
  async #mountProductControls(card) {
    const productPageUrl = this.#getProductPageUrl(card);
    if (!productPageUrl) return;

    this.#currentProductUrl = productPageUrl;

    const html = await this.fetchProductPage(productPageUrl);
    if (!html) return;

    const isPDP = window.location.pathname.includes("/products/");
    this.#updateContentsFromHtml(html);

    // If ratings weren't populated from the card (e.g., nugget without metafields),
    // try to get them from the product page HTML
    this.#updateRatingsFromHtml(html);

    await this.#mountVariantPicker(html);

    // Skip product options (subscription/one-time selector) for gift-boxes
    if (!this.#isGiftBoxQuickView) {
      await this.#mountProductOptions(html, isPDP);
    }

    await this.#mountProductForm(html);

    // Options mount before the form; the form can miss the initial selling plan emit.
    this.#reEmitPurchaseSelectionForForm();
  }

  /**
   * Refresh `gr-product-options` from the latest PDP HTML.
   * Preserves selection and frequency when possible.
   * @param {Document} html
   */
  async #refreshProductOptionsFromHtml(html) {
    const host = this.refs.optionsHost;
    if (!(host instanceof HTMLElement)) return;

    const current = host.querySelector("gr-product-options");
    const wasSubscription =
      current
        ?.querySelector('[ref="subscriptionOption"]')
        ?.getAttribute("data-selected") === "true";
    const currentFrequencySelect = current?.querySelector(
      '[ref="frequencySelect"]',
    );
    const previousFrequency =
      currentFrequencySelect instanceof HTMLSelectElement
        ? currentFrequencySelect.value
        : null;

    const next = html.querySelector("gr-product-options");
    if (!next) return;

    const clone = /** @type {HTMLElement} */ (next.cloneNode(true));
    clone.id = "quick-view-product-options";
    this.#namespaceIds(clone);

    host.innerHTML = "";
    this.#optionsElement = clone;
    host.appendChild(clone);

    const tagName = clone.localName;
    await customElements.whenDefined(tagName);
    customElements.upgrade(clone);

    this.#applyQuickViewFrequencyOptionLabels(clone);

    const freqModal = clone.querySelector("gr-frequency-modal");
    if (freqModal) {
      await customElements.whenDefined("gr-frequency-modal");
      customElements.upgrade(freqModal);
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (wasSubscription) {
      const upgraded = /** @type {GRProductOptionsElement} */ (clone);
      upgraded.selectSubscription?.(null, true);

      const freqSelect = clone.querySelector('[ref="frequencySelect"]');
      if (previousFrequency && freqSelect instanceof HTMLSelectElement) {
        freqSelect.value = previousFrequency;
        freqSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else {
      const upgraded = /** @type {GRProductOptionsElement} */ (clone);
      upgraded.selectOneTime?.();
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {string | null}
   */
  #getProductPageUrl(card) {
    const titleLink = card.querySelector('[ref="productTitleLink"]');
    const cardLink = card.querySelector('[ref="productCardLink"]');

    let href = null;
    if (titleLink instanceof HTMLAnchorElement)
      href = titleLink.getAttribute("href");
    if (!href && cardLink instanceof HTMLAnchorElement)
      href = cardLink.getAttribute("href");
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);

      const cardApi = /** @type {ProductCardApi} */ (card);
      const selectedVariantId =
        cardApi.getSelectedVariantId?.() ||
        (card.querySelector(
          'input[type="radio"]:checked[data-variant-id]',
        ) instanceof HTMLInputElement
          ? /** @type {HTMLInputElement} */ (
              card.querySelector('input[type="radio"]:checked[data-variant-id]')
            ).dataset.variantId
          : null);

      if (selectedVariantId)
        url.searchParams.set("variant", String(selectedVariantId));
      return `${url.pathname}${url.search}`;
    } catch {
      return href;
    }
  }

  /**
   * @param {Document} html
   */
  async #mountVariantPicker(html) {
    const host = this.refs.variantHost;
    if (!(host instanceof HTMLElement)) return;

    host.innerHTML = "";

    const pickerEl = html.querySelector(
      "variant-picker.gr-variant-picker, variant-picker",
    );
    if (!pickerEl) return;

    const clone = /** @type {HTMLElement} */ (pickerEl.cloneNode(true));
    clone.id = "quick-view-variant-picker";

    // Prevent variant-picker from treating this as the real PDP and morphing main/URL.
    clone.removeAttribute("data-template-product-match");

    // Prevent id/name collisions with any existing PDP controls.
    this.#namespaceIds(clone);
    this.#namespaceVariantPicker(clone);

    host.appendChild(clone);

    await customElements.whenDefined("variant-picker");
    customElements.upgrade(clone);
  }

  /**
   * @param {Document} html
   * @param {boolean} [isPDP=false]
   */
  async #mountProductOptions(html, isPDP = false) {
    const host = this.refs.optionsHost;
    if (!(host instanceof HTMLElement)) return;

    host.innerHTML = "";
    this.#optionsElement = null;

    const optionsEl = html.querySelector("gr-product-options");
    if (!optionsEl) return;

    const clone = /** @type {HTMLElement} */ (optionsEl.cloneNode(true));
    clone.id = "quick-view-product-options";
    clone.setAttribute("data-quick-view", "true");

    // Prevent id collisions (e.g. frequency select id/label) with the PDP.
    this.#namespaceIds(clone);

    this.#optionsElement = clone;
    host.appendChild(clone);

    await customElements.whenDefined("gr-product-options");
    customElements.upgrade(clone);

    this.#applyQuickViewFrequencyOptionLabels(clone);

    const freqModal = clone.querySelector("gr-frequency-modal");
    if (freqModal) {
      await customElements.whenDefined("gr-frequency-modal");
      customElements.upgrade(freqModal);
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Intelligems cohort sync: rewrite every [data-selling-plan-id] in the
    // mounted options to use the cohort-duplicate's plan ids. Must run before
    // selectSubscription below so the initial SellingPlanUpdateEvent already
    // carries the cohort plan id (otherwise the form's hidden selling_plan
    // input briefly holds a control plan and the user's first ATC would 422).
    const igPlanSync = /** @type {any} */ (window).IGPlanSync;
    const controlHandle = this.#extractControlHandleFromCurrentUrl();
    if (igPlanSync?.remapSellingPlanIdsInScope && controlHandle) {
      try {
        await igPlanSync.remapSellingPlanIdsInScope(clone, controlHandle);
      } catch (err) {
        console.warn("[quick-view] IG plan remap failed (non-fatal):", err);
      }
    }

    const upgraded = /** @type {GRProductOptionsElement} */ (clone);
    upgraded.selectSubscription?.(null, true);
  }

  /**
   * Pulls the product handle out of `#currentProductUrl` (e.g.
   * `/products/ranchers-classic?variant=123` → `ranchers-classic`). Used to
   * key the Intelligems cohort plan-id remap.
   *
   * @returns {string | null}
   */
  #extractControlHandleFromCurrentUrl() {
    const url = this.#currentProductUrl;
    if (!url) return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Quick View-only: show eater-frequency hints (3+, 2, 1) in the delivery frequency dropdown.
   * @param {HTMLElement} optionsRoot
   */
  #applyQuickViewFrequencyOptionLabels(optionsRoot) {
    const select = optionsRoot.querySelector('[ref="frequencySelect"]');
    if (!(select instanceof HTMLSelectElement)) return;

    const freqModal = optionsRoot.querySelector("gr-frequency-modal");
    if (!(freqModal instanceof HTMLElement)) return;

    /** @type {Map<string, string>} */
    const householdLabelByFrequency = new Map();
    for (const el of freqModal.querySelectorAll(
      "button[data-frequency][data-household-label]",
    )) {
      if (!(el instanceof HTMLButtonElement)) continue;
      const frequency = el.dataset.frequency;
      const label = el.dataset.householdLabel;
      if (!frequency || !label) continue;
      householdLabelByFrequency.set(frequency, label);
    }

    for (let idx = 0; idx < select.options.length; idx += 1) {
      const option = select.options.item(idx);
      if (!option) continue;
      const baseLabel = option.dataset.qvBaseLabel || option.textContent || "";
      option.dataset.qvBaseLabel = baseLabel;

      const suffix = householdLabelByFrequency.get(option.value) || null;
      if (!suffix) {
        option.textContent = baseLabel;
        continue;
      }

      option.textContent = `${baseLabel} (${suffix})`;
    }
  }

  /**
   * Check if nugget form host is available
   * @returns {nuggetHost is HTMLElement}
   */
  #hasNuggetFormHost() {
    const nuggetHost = this.refs.nuggetFormHost;
    return this.#isNuggetQuickView && nuggetHost instanceof HTMLElement;
  }

  /**
   * Toggle satisfaction guaranteed visibility based on nugget vs regular product
   * Also hides for gift-box products
   * @param {boolean} isNugget
   */
  #toggleSatisfactionGuarantee(isNugget) {
    const nuggetSatisfaction = this.refs.nuggetSatisfactionGuarantee;
    const regularSatisfaction = this.refs.regularSatisfactionGuarantee;

    // Hide both satisfaction guarantees for gift-boxes
    if (this.#isGiftBoxQuickView) {
      if (nuggetSatisfaction instanceof HTMLElement) {
        nuggetSatisfaction.classList.add("hidden");
      }
      if (regularSatisfaction instanceof HTMLElement) {
        regularSatisfaction.classList.add("hidden");
      }
      return;
    }

    if (nuggetSatisfaction instanceof HTMLElement) {
      nuggetSatisfaction.classList.toggle("hidden", !isNugget);
    }
    if (regularSatisfaction instanceof HTMLElement) {
      regularSatisfaction.classList.toggle("hidden", isNugget);
    }
  }

  /**
   * Mount PDP add-to-cart form so cart behavior matches product pages.
   * @param {Document} html
   */
  async #mountProductForm(html) {
    const host = this.refs.formHost;
    const footerHost = this.refs.formHostFooter;
    const nuggetHost = this.refs.nuggetFormHost;
    if (!(host instanceof HTMLElement)) return;
    if (!(footerHost instanceof HTMLElement)) return;

    host.innerHTML = "";
    footerHost.innerHTML = "";

    // Gift-box case: mount simple form in footer
    if (this.#isGiftBoxQuickView) {
      host.classList.add("hidden");
      footerHost.classList.remove("hidden");
      if (nuggetHost instanceof HTMLElement) {
        nuggetHost.classList.add("hidden");
      }

      const formEl = html.querySelector(
        "product-form-component.gr-add-to-cart__product-form, product-form-component",
      );
      if (!formEl) return;

      const clone = /** @type {HTMLElement} */ (formEl.cloneNode(true));
      clone.id = "quick-view-product-form";
      clone.setAttribute("data-cart-source-context", "quick-view");
      this.#namespaceIds(clone);
      footerHost.appendChild(clone);

      await customElements.whenDefined("product-form-component");
      customElements.upgrade(clone);
      return;
    }

    // Nugget case: mount nugget-specific buttons in nuggetFormHost
    if (this.#hasNuggetFormHost()) {
      /** @type {HTMLElement} */ (nuggetHost).innerHTML = "";
      /** @type {HTMLElement} */ (nuggetHost).classList.remove("hidden");
      host.classList.add("hidden");
      footerHost.classList.add("hidden");
      this.#toggleSatisfactionGuarantee(true);

      const nuggetForm = html.querySelector(".gr-nugget-add-to-cart");
      if (nuggetForm) {
        const clone = /** @type {HTMLElement} */ (nuggetForm.cloneNode(true));
        this.#namespaceIds(clone);
        /** @type {HTMLElement} */ (nuggetHost).appendChild(clone);

        const formComponent = clone.querySelector("product-form-component");
        if (formComponent) {
          await customElements.whenDefined("product-form-component");
          customElements.upgrade(formComponent);
        }

        this.#setupNuggetButtonToggle(clone);
        return;
      }
    }

    // Regular product case: mount form in body
    if (nuggetHost instanceof HTMLElement) {
      nuggetHost.classList.add("hidden");
    }
    host.classList.remove("hidden");
    footerHost.classList.add("hidden");
    this.#toggleSatisfactionGuarantee(false);

    const formEl = html.querySelector(
      "product-form-component.gr-add-to-cart__product-form, product-form-component",
    );
    if (!formEl) return;

    const clone = /** @type {HTMLElement} */ (formEl.cloneNode(true));
    clone.id = "quick-view-product-form";
    clone.setAttribute("data-cart-source-context", "quick-view");
    clone.classList.remove("hidden"); // add this line
    this.#namespaceIds(clone);

    host.appendChild(clone);

    await customElements.whenDefined("product-form-component");
    customElements.upgrade(clone);

    // Upgrade add-to-cart-component so it shows the button
    const addToCart = clone.querySelector("add-to-cart-component");
    if (addToCart instanceof HTMLElement) {
      await customElements.whenDefined("add-to-cart-component");
      customElements.upgrade(addToCart);
    }

    const btn = clone.querySelector('[ref="addToCartButton"]');
    if (btn instanceof HTMLElement) {
      btn.classList.remove("hidden");
      btn.className =
        "custom-button custom-button--primary gr-flex flex-row justify-center items-center px-6 py-2.5 gap-2 mt-3 h-[58px] w-full rounded font-ringside-ssm-bold text-base capitalize transition-colors duration-200 locked-addons-cta cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden";
    }
    this.#reEmitPurchaseSelectionForForm();
    this.#updateAddToCartButtonText();
  }

  /**
   * Set up nugget button toggle based on bundle/one-time selection
   * @param {HTMLElement} container
   */
  #setupNuggetButtonToggle(container) {
    const bundleBtn = container.querySelector(
      "#nugget-bundle-btn, [id*='nugget-bundle-btn']",
    );
    const onetimeForm = container.querySelector(
      "#nugget-onetime-form, [id*='nugget-onetime-form']",
    );

    // Listen for option changes from gr-product-options
    const optionsHost = this.refs.optionsHost;
    if (!(optionsHost instanceof HTMLElement)) return;

    const updateButtons = () => {
      const options = optionsHost.querySelector(
        "gr-product-options",
      );
      if (!options) return;

      const subscriptionOption = options.querySelector(
        '[ref="subscriptionOption"]',
      );
      const isBundleSelected =
        subscriptionOption?.getAttribute("data-selected") === "true";

      if (
        bundleBtn instanceof HTMLElement &&
        onetimeForm instanceof HTMLElement
      ) {
        bundleBtn.classList.toggle("hidden", !isBundleSelected);
        onetimeForm.classList.toggle("hidden", isBundleSelected);
      }
    };

    // Initial update
    updateButtons();

    // Listen for changes
    optionsHost.addEventListener("click", () => {
      requestAnimationFrame(updateButtons);
    });
  }

  /**
   * Refresh mounted product form from the latest variant HTML.
   * @param {Document} html
   */
  async #refreshProductFormFromHtml(html) {
    const host = this.refs.formHost;
    if (!(host instanceof HTMLElement)) return;
    if (!host.querySelector("product-form-component")) return;
    await this.#mountProductForm(html);
  }

  /**
   * Namespace ids inside a cloned fragment so `id`/`for` don't collide with the page.
   * @param {HTMLElement} root
   */
  #namespaceIds(root) {
    const token = `${this.#namespaceToken}-${this.id || "drawer"}`;
    /** @type {Map<string, string>} */
    const idMap = new Map();

    for (const el of root.querySelectorAll("[id]")) {
      if (!(el instanceof HTMLElement)) continue;
      const id = el.id;
      if (!id) continue;
      const next = `${id}--${token}`;
      idMap.set(id, next);
      el.id = next;
    }

    for (const label of root.querySelectorAll("label[for]")) {
      if (!(label instanceof HTMLLabelElement)) continue;
      const current = label.htmlFor;
      const next = idMap.get(current);
      if (next) label.htmlFor = next;
    }

    // Handle simple hash links if present.
    for (const a of root.querySelectorAll('a[href^="#"]')) {
      if (!(a instanceof HTMLAnchorElement)) continue;
      const href = a.getAttribute("href") || "";
      const raw = href.slice(1);
      const next = idMap.get(raw);
      if (next) a.setAttribute("href", `#${next}`);
    }
  }

  /**
   * Namespace radio `name`s to avoid cross-document grouping.
   * @param {HTMLElement} root
   */
  #namespaceVariantPicker(root) {
    const token = `${this.#namespaceToken}-${this.id || "drawer"}`;

    for (const input of root.querySelectorAll('input[type="radio"][name]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const name = input.name;
      if (!name) continue;
      if (name.includes(`--${token}`)) continue;
      input.name = `${name}--${token}`;
    }
  }

  /** @param {VariantResource | null | undefined} resource */
  #updateGalleryForVariant(resource) {
    const featuredMediaId = resource?.featured_media?.id;
    if (!featuredMediaId) return;

    const galleryHost = this.refs.galleryHost;
    if (!(galleryHost instanceof HTMLElement)) return;

    const mediaId = String(featuredMediaId);

    const slides = galleryHost.querySelectorAll(
      "slideshow-slide[variant-image]",
    );
    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;
      slide.hidden = slide.getAttribute("slide-id") !== mediaId;
    }

    const slideshow = /** @type {SlideshowComponentElement | null} */ (
      galleryHost.querySelector("slideshow-component")
    );
    slideshow?.select?.({ id: mediaId }, undefined, { animate: false });
  }

  /** @param {Document} html */
  #updateContentsFromHtml(html) {
    const contentsHost = this.refs.contentsHost;
    if (!(contentsHost instanceof HTMLElement)) return;

    // Source of truth for box contents: GR Value Props "WHAT'S IN THE BOX" accordion.
    /** @type {HTMLElement | null} */
    let details = /** @type {HTMLElement | null} */ (
      html.querySelector('details[data-block-id="gr-accordion-whats-in-box"]')
    );

    if (!details) {
      for (const el of Array.from(
        html.querySelectorAll("details.gr-product-accordion"),
      )) {
        const heading = el.querySelector('[role="heading"]')?.textContent || "";
        if (
          heading.replace(/\s+/g, " ").trim().toUpperCase() ===
          "WHAT'S IN THE BOX"
        ) {
          details = /** @type {HTMLElement} */ (el);
          break;
        }
      }
    }

    if (!details) return;

    const items = Array.from(details.querySelectorAll("ul li p"))
      .map((p) => (p.textContent || "").replace(/\s+/g, " ").trim())
      .filter(Boolean);

    if (!items.length) return;

    const wrapper = document.createElement("div");
    wrapper.className = this.#isNuggetQuickView
      ? "box-contents-wrapper box-contents-wrapper--nugget"
      : "box-contents-wrapper";

    const ul = document.createElement("ul");

    for (const text of items) {
      const li = document.createElement("li");
      const bullet = document.createElement("span");
      bullet.textContent = "•";
      const body = document.createElement("span");
      body.textContent = text;
      li.appendChild(bullet);
      li.appendChild(body);
      ul.appendChild(li);
    }

    wrapper.appendChild(ul);
    contentsHost.innerHTML = "";
    contentsHost.appendChild(wrapper);
  }

  /**
   * Populate ratings from product page HTML if not already rendered from the card.
   * This handles cases like nugget products where metafields may not be set.
   * @param {Document} html
   */
  #updateRatingsFromHtml(html) {
    const ratingsHost = this.refs.ratingsHost;
    if (!(ratingsHost instanceof HTMLElement)) return;

    // Skip if ratings were already populated from the card
    if (ratingsHost.children.length > 0) {
      console.log("[QuickView] Ratings already populated from card");
      return;
    }

    // Look for product-card-reviews block in the PDP HTML
    // This contains the Alpine-based reviews with data attributes
    const reviewsBlock = html.querySelector('[x-data^="productCardReviews"]');
    console.log("[QuickView] reviewsBlock found:", !!reviewsBlock);

    let rating = 0;
    let count = 0;

    if (reviewsBlock) {
      // Extract rating data from the x-data attribute
      // Format: productCardReviews('SKU', 4.5, 123)
      const xData = reviewsBlock.getAttribute("x-data") || "";
      console.log("[QuickView] x-data:", xData);
      const match = xData.match(
        /productCardReviews\s*\(\s*['"][^'"]*['"]\s*,\s*([\d.]+)\s*,\s*(\d+)\s*\)/,
      );
      console.log("[QuickView] regex match:", match);
      if (match && match[1] && match[2]) {
        rating = parseFloat(match[1]);
        count = parseInt(match[2], 10);
      }
    }

    // If no Alpine block found, try looking in noscript fallback
    if ((!rating || !count) && html.querySelector("noscript")) {
      const noscript = html.querySelector(
        "noscript .product-card-reviews-stars",
      );
      console.log("[QuickView] noscript stars found:", !!noscript);
      if (noscript) {
        const ariaLabel = noscript.getAttribute("aria-label") || "";
        const ratingMatch = ariaLabel.match(/([\d.]+)\s+out\s+of\s+5/i);
        if (ratingMatch && ratingMatch[1]) {
          rating = parseFloat(ratingMatch[1]);
        }
        const countEl =
          noscript.parentElement?.querySelector("span:last-child");
        const countText = countEl?.textContent || "";
        const countMatch = countText.match(/\((\d+)\)/);
        if (countMatch && countMatch[1]) {
          count = parseInt(countMatch[1], 10);
        }
      }
    }

    // Also try static fallback (non-Alpine version)
    if (!rating || !count) {
      const staticReviews = html.querySelector(
        ".product-card-reviews-stars[aria-label]",
      );
      console.log("[QuickView] static reviews found:", !!staticReviews);
      if (staticReviews) {
        const ariaLabel = staticReviews.getAttribute("aria-label") || "";
        const ratingMatch = ariaLabel.match(/([\d.]+)\s+out\s+of\s+5/i);
        if (ratingMatch && ratingMatch[1]) {
          rating = parseFloat(ratingMatch[1]);
        }
        const countEl =
          staticReviews.parentElement?.querySelector("span:last-child");
        const countText = countEl?.textContent || "";
        const countMatch = countText.match(/\((\d+)\)/);
        if (countMatch && countMatch[1]) {
          count = parseInt(countMatch[1], 10);
        }
      }
    }

    console.log("[QuickView] Final rating:", rating, "count:", count);
    // Render ratings even if 0 - let the UI show empty stars / (0) count

    // Calculate star breakdown
    const fullStars = Math.floor(rating);
    const decimal = rating - fullStars;
    let hasHalfStar = false;
    let hasExtraFull = false;

    if (decimal >= 0.6) {
      hasExtraFull = true;
    } else if (decimal >= 0.3) {
      hasHalfStar = true;
    }

    let totalFull = fullStars;
    if (hasExtraFull) {
      totalFull = Math.min(fullStars + 1, 5);
    }

    let remainingAfterFull = 5 - totalFull;
    let emptyStars;
    if (hasHalfStar && totalFull < 5) {
      emptyStars = remainingAfterFull - 1;
      emptyStars = Math.max(0, emptyStars);
    } else {
      emptyStars = remainingAfterFull;
      hasHalfStar = false;
    }

    // Build the ratings display
    const wrapper = document.createElement("div");
    wrapper.className = "!flex !items-center !gap-1";

    const starsContainer = document.createElement("div");
    starsContainer.className = "product-card-reviews-stars !flex !items-center";
    starsContainer.setAttribute("role", "img");
    starsContainer.setAttribute("aria-label", `${rating} out of 5 stars`);

    // Get star SVG templates from the page
    const starClasses =
      "!w-4 !h-4 lg:!w-[18px] lg:!h-[18px] [&_svg]:!w-full [&_svg]:!h-full";

    // Try to get SVG content from template tags in the Alpine-based reviews
    // Templates contain the actual star markup
    let fullStarHtml = "";
    let halfStarHtml = "";
    let emptyStarHtml = "";

    // Look in Alpine template tags
    const templates = html.querySelectorAll(
      '[x-data^="productCardReviews"] template',
    );
    for (const template of templates) {
      const content = /** @type {HTMLTemplateElement} */ (template).content;
      const span = content.querySelector("span");
      if (!span) continue;

      const xFor = template.getAttribute("x-for") || "";
      const xIf = template.getAttribute("x-if") || "";

      if (xFor.includes("fullStars")) {
        fullStarHtml = span.innerHTML;
      } else if (xIf.includes("hasHalfStar")) {
        halfStarHtml = span.innerHTML;
      } else if (xFor.includes("emptyStars")) {
        emptyStarHtml = span.innerHTML;
      }
    }

    // Fallback: look in noscript or static fallback sections
    if (!fullStarHtml) {
      const noscriptStars = html.querySelector(
        "noscript .product-card-reviews-stars span",
      );
      fullStarHtml = noscriptStars?.innerHTML || "";
    }
    if (!fullStarHtml) {
      const staticStars = html.querySelector(
        ".product-card-reviews-stars span",
      );
      fullStarHtml = staticStars?.innerHTML || "";
    }

    // Final fallback to text symbols
    if (!fullStarHtml) fullStarHtml = "★";
    if (!halfStarHtml) halfStarHtml = fullStarHtml; // Use full star as fallback for half
    if (!emptyStarHtml) emptyStarHtml = "☆";

    // Add full stars
    for (let i = 0; i < totalFull; i++) {
      const span = document.createElement("span");
      span.className = starClasses;
      span.setAttribute("aria-hidden", "true");
      span.innerHTML = fullStarHtml;
      starsContainer.appendChild(span);
    }

    // Add half star if applicable
    if (hasHalfStar) {
      const span = document.createElement("span");
      span.className = starClasses;
      span.setAttribute("aria-hidden", "true");
      span.innerHTML = halfStarHtml;
      starsContainer.appendChild(span);
    }

    // Add empty stars
    for (let i = 0; i < emptyStars; i++) {
      const span = document.createElement("span");
      span.className = starClasses;
      span.setAttribute("aria-hidden", "true");
      span.innerHTML = emptyStarHtml;
      starsContainer.appendChild(span);
    }

    wrapper.appendChild(starsContainer);

    // Add the count
    const countSpan = document.createElement("span");
    countSpan.className = "!text-sm !text-gray-1100";
    countSpan.textContent = `(${count})`;
    wrapper.appendChild(countSpan);

    ratingsHost.appendChild(wrapper);
  }

  /**
   * @param {HTMLElement} card
   */
  #render(card) {
    const galleryHost = this.refs.galleryHost;
    const pricingHost = this.refs.pricingHost;
    const titleHost = this.refs.titleHost;
    const priceHost = this.refs.priceHost;
    const ratingsHost = this.refs.ratingsHost;
    const proteinsHost = this.refs.proteinsHost;
    const contentsHost = this.refs.contentsHost;
    if (!(galleryHost instanceof HTMLElement)) return;
    if (!(pricingHost instanceof HTMLElement)) return;
    if (!(titleHost instanceof HTMLElement)) return;
    if (!(priceHost instanceof HTMLElement)) return;
    if (!(ratingsHost instanceof HTMLElement)) return;
    if (!(proteinsHost instanceof HTMLElement)) return;
    if (!(contentsHost instanceof HTMLElement)) return;

    galleryHost.innerHTML = "";
    titleHost.innerHTML = "";
    priceHost.innerHTML = "";
    ratingsHost.innerHTML = "";
    proteinsHost.innerHTML = "";
    contentsHost.innerHTML = "";

    const gallery = card.querySelector(".card-gallery");
    const title = card.querySelector('[ref="productTitleLink"]');
    const pricing = card.querySelector("product-price");
    // Look for Alpine-based reviews or static nugget ratings
    const ratings =
      card.querySelector('[x-data^="productCardReviews"]') ||
      card.querySelector(".nugget-quick-view-ratings");
    const proteins = card.querySelector(".protein-pills");
    const contents = card.querySelector(".box-contents-wrapper");

    if (gallery) {
      const galleryClone = /** @type {HTMLElement} */ (gallery.cloneNode(true));
      galleryHost.append(galleryClone);

      // Upgrade slideshow components so they become interactive
      const slideshow = galleryClone.querySelector("slideshow-component");
      if (slideshow) {
        customElements.whenDefined("slideshow-component").then(() => {
          customElements.upgrade(slideshow);
        });
      }
    }
    this.#renderTitle(titleHost, title, card);
    this.#renderPrice(priceHost, pricing);
    if (ratings) this.#renderRatings(ratingsHost, ratings);
    if (proteins) proteinsHost.append(proteins.cloneNode(true));
    if (contents) contentsHost.append(contents.cloneNode(true));
  }

  /**
   * Render price with appropriate styling for nugget vs regular products
   * @param {HTMLElement} priceHost
   * @param {Element | null} pricing
   * @param {HTMLElement | null} [nuggetCard]
   */
  #renderPrice(priceHost, pricing, nuggetCard = null) {
    if (!pricing) return;

    const isNugget = this.#isNuggetQuickView;
    const salePriceEl = pricing.querySelector(".price");
    const comparePriceEl = pricing.querySelector(".compare-at-price");

    const salePrice = salePriceEl?.textContent?.trim() || "";
    const comparePrice = comparePriceEl?.textContent?.trim() || "";

    const priceContainer = pricing.querySelector('[ref="priceContainer"]');
    const productId =
      pricing.dataset?.productId || priceContainer?.dataset?.productId || "";
    const variantId =
      priceContainer?.dataset?.variantId || pricing.dataset?.variantId || "";

    const priceWrapper = document.createElement("div");
    priceWrapper.className = isNugget
      ? "gr-flex gap-[8px] mt-[8px]"
      : "gr-flex flex-row items-center gap-2 text-right";
    priceWrapper.setAttribute("data-testid", "product-price");
    if (productId) priceWrapper.setAttribute("data-product-id", productId);
    if (variantId) priceWrapper.setAttribute("data-variant-id", variantId);

    const salePriceDiv = document.createElement("div");
    salePriceDiv.className = isNugget
      ? "font-ringside-compressed-ssm-medium font-[900] leading-[24px] text-black-warm text-[18px] lg:text-[22px] igComparePrice"
      : "font-knockout-71 text-[28px] leading-[32px] text-black-warm igComparePrice";
    salePriceDiv.textContent = salePrice;
    priceWrapper.appendChild(salePriceDiv);

    if (comparePrice && comparePrice !== salePrice) {
      const comparePriceDiv = document.createElement("div");
      comparePriceDiv.className = isNugget
        ? "text-grayscale-coal-500 line-through text-[12px] lg:text-[14px] items-center flex igPrice"
        : "text-gray-900 line-through text-md igPrice";
      comparePriceDiv.textContent = comparePrice;
      priceWrapper.appendChild(comparePriceDiv);
    }

    priceHost.appendChild(priceWrapper);
  }

  /**
   * Render star ratings from product card reviews component
   * @param {HTMLElement} ratingsHost
   * @param {Element} ratingsEl
   */
  #renderRatings(ratingsHost, ratingsEl) {
    // For Alpine-based reviews, we need to extract just the visible content
    // to avoid template duplication when Alpine re-initializes on the clone
    const starsContainer = ratingsEl.querySelector(
      ".product-card-reviews-stars",
    );
    const countEl =
      ratingsEl.querySelector("[x-text]") ||
      ratingsEl.querySelector("span:last-child");

    if (!starsContainer) {
      // Fallback: just clone the whole thing (for static nugget ratings)
      ratingsHost.appendChild(ratingsEl.cloneNode(true));
      return;
    }

    // Create a static snapshot of the ratings
    const wrapper = document.createElement("div");
    wrapper.className = "!flex !items-center !gap-1";

    // Clone only the actual star spans (not the template tags)
    const starsClone = document.createElement("div");
    starsClone.className = starsContainer.className;
    starsClone.setAttribute("role", "img");
    starsClone.setAttribute(
      "aria-label",
      starsContainer.getAttribute("aria-label") || "",
    );

    // Only copy actual span elements, skip template tags
    for (const child of starsContainer.children) {
      if (child.tagName === "SPAN") {
        starsClone.appendChild(child.cloneNode(true));
      }
    }

    wrapper.appendChild(starsClone);

    // Add the count
    if (countEl) {
      const countClone = document.createElement("span");
      countClone.className = "!text-sm !text-gray-1100";
      countClone.textContent = countEl.textContent || "";
      wrapper.appendChild(countClone);
    }

    ratingsHost.appendChild(wrapper);
  }

  /**
   * @param {HTMLElement} titleHost
   * @param {Element | null} titleEl
   * @param {HTMLElement} card
   */
  #renderTitle(titleHost, titleEl, card) {
    const text = (
      titleEl?.textContent ||
      card.querySelector(".product-card__link .visually-hidden")?.textContent ||
      ""
    )
      .replace(/\s+/g, " ")
      .trim();

    if (!text) return;

    /** @type {string | null} */
    let href = null;
    if (titleEl instanceof HTMLAnchorElement)
      href = titleEl.getAttribute("href");
    if (!href) {
      const cardLink = card.querySelector('[ref="productCardLink"]');
      if (cardLink instanceof HTMLAnchorElement)
        href = cardLink.getAttribute("href");
    }

    const link = document.createElement("a");
    link.className = "quick-view-drawer__title-link";
    link.textContent = text;
    if (href) link.href = href;

    titleHost.append(link);
  }

  handleViewDetails() {
    const formHost = this.refs.formHost;
    const formComponent =
      formHost instanceof HTMLElement
        ? formHost.querySelector("product-form-component")
        : null;

    const productUrl =
      (formComponent instanceof HTMLElement &&
        formComponent.dataset.productUrl) ||
      this.#currentProductUrl;
    if (!productUrl) return;

    const idInput =
      formComponent instanceof HTMLElement
        ? formComponent.querySelector('input[name="id"]')
        : null;
    const variantId =
      idInput instanceof HTMLInputElement ? idInput.value : null;

    try {
      const url = new URL(productUrl, window.location.origin);
      if (variantId) url.searchParams.set("variant", variantId);
      window.location.assign(url.toString());
    } catch {
      window.location.assign(productUrl);
    }
  }

  /**
   * Initialize the Nugget "How it Works" accordion.
   * Shows/hides based on nugget mode and sets default open state based on viewport.
   */
  #initNuggetAccordion() {
    // Abort previous accordion listeners to prevent memory leaks
    this.#accordionAbortController?.abort();
    this.#accordionAbortController = new AbortController();

    const host = this.refs.nuggetHowItWorksHost;
    if (!(host instanceof HTMLElement)) return;

    if (this.#isNuggetQuickView) {
      host.classList.remove("hidden");

      // Default open on desktop (>=1024px), closed on mobile
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      this.#setAccordionOpen(isDesktop);

      // Set up toggle button with abort signal
      const toggle = host.querySelector('[ref="nuggetHowItWorksToggle"]');
      if (toggle instanceof HTMLElement) {
        toggle.addEventListener("click", this.#handleAccordionToggle, {
          signal: this.#accordionAbortController.signal,
        });
      }
    } else {
      host.classList.add("hidden");
    }
  }

  /**
   * Handle accordion toggle click
   */
  #handleAccordionToggle = () => {
    const host = this.refs.nuggetHowItWorksHost;
    if (!(host instanceof HTMLElement)) return;

    const isCurrentlyOpen = host.classList.contains("open");
    this.#setAccordionOpen(!isCurrentlyOpen);
  };

  /**
   * Set accordion open/closed state
   * @param {boolean} open
   */
  #setAccordionOpen(open) {
    const host = this.refs.nuggetHowItWorksHost;
    if (!(host instanceof HTMLElement)) return;

    const toggle = host.querySelector('[ref="nuggetHowItWorksToggle"]');

    if (open) {
      host.classList.add("open");
      toggle?.setAttribute("aria-expanded", "true");
    } else {
      host.classList.remove("open");
      toggle?.setAttribute("aria-expanded", "false");
    }
  }

  /**
   * Apply gift-box specific styling - hide subscription options, satisfaction guarantee, and view details
   */
  #applyGiftBoxStyles() {
    if (!this.#isGiftBoxQuickView) return;

    // Hide view details button
    const viewDetailsWrapper = this.refs.viewDetailsWrapper;
    if (viewDetailsWrapper instanceof HTMLElement) {
      viewDetailsWrapper.style.display = "none";
    }

    // Hide both satisfaction guarantee sections
    const nuggetSatisfaction = this.refs.nuggetSatisfactionGuarantee;
    const regularSatisfaction = this.refs.regularSatisfactionGuarantee;

    if (nuggetSatisfaction instanceof HTMLElement) {
      nuggetSatisfaction.style.display = "none";
    }
    if (regularSatisfaction instanceof HTMLElement) {
      regularSatisfaction.style.display = "none";
    }
  }
}

if (!customElements.get("quick-view-drawer")) {
  customElements.define("quick-view-drawer", QuickViewDrawer);
}
